/* globals FB */

/**
 * @file
 * A cross-platform helper for initializing and making FB calls through
 * our public-api proxy.
 */

import Context from "shared/util/Context";
import OkC from "shared/util/OkcApi";
import loadjs from "shared/util/loadjs";
import { hasPhoneCommand } from "shared/util/AppPhoneCommand";

const _data = {
	js: "//connect.facebook.com/en_US/sdk.js",
	scope: "email,user_birthday,user_photos",
	appId: null,
	version: "v2.9",
	loaded: false,
	isConnected: false,
};

function _addFbDiv() {
	const fbDiv = document.createElement("div");
	document.body.appendChild(fbDiv);
}

function _getToken() {
	// apps have a slightly different flow
	if (hasPhoneCommand("doFacebookLogin")) {
		return new Promise((resolve, reject) => {
			const callbackName = "FacebookConnectAppCallback";

			// make a callback function attached to the window
			if (!window[callbackName]) {
				window[callbackName] = (resJSON) => {
					let res = resJSON;

					if (typeof res === "string") {
						res = JSON.parse(res);
					}

					window.App.pushActionHistory("Facebook.connect", [res]);

					if (res.access_token) {
						// Phone only gives back { access_token: "" }, so juke it.
						resolve({
							status: "connected",
							authResponse: {
								accessToken: res.access_token,
							},
						});
					}
					else {
						reject(res);
					}
				};
			}

			// issue the phone command
			window.App.phoneCommand("doFacebookLogin", {
				cb: callbackName,
				parameters: {
					permissions: _data.scope.split(","),
				},
			});
		});
	} else {
		return new Promise((resolve) => {
			FB.login((res) => resolve(res), {
				scope: _data.scope,
				auth_type: "rerequest",
			});
		});
	}
}

function _connectToken(res) {
	if (!res || res.status !== "connected") {
		_data.isConnected = false;
		return Promise.reject(res);
	}

	return new Promise((resolve, reject) => {
		OkC.post("/social/fb/connect", {
			version: 2,
			data: { token: res.authResponse.accessToken },
		})
			.then(() => {
				_data.isConnected = false;
				resolve();
			})
			.catch((e) => reject(e));
	});
}

/*
 * Load the Facebook binary.
 * This is REQUIRED.
 * If you try to call this library without loading first, this is
 * going to get really mad at you.
 *
 * @return {Promise}
 */
function load() {
	if (_data.loaded) {
		return;
	}

	_data.appId = Context.get("fbAppId");

	_addFbDiv();

	loadjs(_data.js)
		.then(() => {
			const params = {
				appId: _data.appId,
				version: _data.version,
			};
			_data.loaded = true;

			FB.init(params);
		})
		.catch((err) => console.error(err));
}

/*
 * Connect to facebook.
 *
 * @return {Promise}
 */
function connect() {
	if (_data.isConnected) {
		return Promise.resolve();
	}

	return _getToken().then((res) => _connectToken(res));
}

/*
 * Attempt to login with facebook. Redirect on success.
 *
 * @return {Promise}
 */
function login() {
	return new Promise((resolve, reject) => {
		_getToken().then((res) => {
			OkC.legacy("/login", {
				type: "POST",
				data: {
					facebook_access_token: res.authResponse.accessToken,
					okc_api: 1,
				},
			})
				.then((res2) => {
					if (res2.status === 0 && (res2.screenname || res2.userid)) {
						resolve(res2);
					}
					else {
						reject({
							...res2,
							hasFacebook: res2.actionable,
							accessToken: res.authResponse.accessToken,
						});
					}
				})
				.catch((err) => reject(err));
		})
			.catch((err) => reject(err));
	});
}

const Facebook = {
	load,
	connect,
	login,
};

export default Facebook;



// WEBPACK FOOTER //
// ./src/shared/util/Facebook.js